import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import {
  informationFieldConfigValueSchema,
  patientKeys,
  updateInformation,
  usePatientInformationFields,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import TextArea from "@/components/TextArea/TextArea";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

export const EditInformation = ({
  currentInformation,
  onSuccess,
  patientId,
}: {
  currentInformation: string | null | undefined;
  onSuccess: () => void;
  patientId: string;
}) => {
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      information: currentInformation,
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (information: string | null | undefined) =>
      updateInformation(patientId, information),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const { t } = useLingui();

  const {
    data: conditionalInformationFields,
    isPending: isPendingConditionalInformationFields,
    isError: isErrorConditionalInformationFields,
    error: errorConditionalInformationFields,
  } = usePatientInformationFields();

  if (isPendingConditionalInformationFields) {
    return <Loading message={t`Hämtar information om valbara fält`} />;
  }

  if (isErrorConditionalInformationFields) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information om valbara fält.`} ${deducedError(errorConditionalInformationFields)}`}
      />
    );
  }

  const MIN_ROWS = 3;
  const lengthOfCurrentInformation = currentInformation
    ? currentInformation.split("\n").length
    : null;
  const calculatedRows =
    lengthOfCurrentInformation && lengthOfCurrentInformation >= MIN_ROWS
      ? lengthOfCurrentInformation
      : MIN_ROWS;

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.information);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar allmän information`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row>
            <TextArea
              label={t`Allmän information`}
              showOptionalLabel={
                conditionalInformationFields.GeneralInformation ===
                informationFieldConfigValueSchema.enum.optional
              }
              rows={calculatedRows}
              errorMessage={errors.information?.message}
              {...register("information", {
                required: {
                  value:
                    conditionalInformationFields.GeneralInformation ===
                    informationFieldConfigValueSchema.enum.required,
                  message: t`Ange allmän information`,
                },
              })}
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
